import type { ILegalTextParagraph } from "~/models/ILegalTextParagraph";
import type { IToC } from "~/models/IToC";

/** flatten the tree structure to a flat list */
const flattenChildren: (item: IToC) => (ILegalTextParagraph | IToC)[] = (
  item
) => {
  if (item.children?.length) {
    return [
      item,
      ...(item.legal_text_paragraphs ?? []),
      ...item.children.flatMap((child) => flattenChildren(child)),
    ];
  }
  // guard against data like the prodhaftg where the response consists of an array of only one object that only contains legal_text_paragraphs as its key
  if (item.id) {
    return [item, ...(item.legal_text_paragraphs ?? [])];
  } else {
    return item.legal_text_paragraphs;
  }
};

export const parentsOfParagraph = (
  flatToc?: (IToC | ILegalTextParagraph)[],
  slug?: string
) => {
  if (!flatToc) {
    return [];
  }

  // reverse here so we don't have to use .findLast which is not supported on Safari < 15
  const reversed = [...flatToc].reverse();

  const para = reversed.findIndex(
    (item) => isParagraph(item) && item.slug === slug
  );

  if (para < 0) {
    return [];
  }

  const lastTocEntry = reversed.find(
    (item, index) => !isParagraph(item) && index > para
  ) as IToC | undefined;

  if (!lastTocEntry?.path) {
    return [];
  }

  const parentIds = lastTocEntry.path.split(".").map((id) => parseInt(id, 10));

  return flatToc.filter(
    (item) =>
      !isParagraph(item) &&
      "id" in item &&
      item.id &&
      parentIds.includes(item.id)
  ) as IToC[];
};

const isParagraph = (
  item: IToC | ILegalTextParagraph
): item is ILegalTextParagraph => "paragraph" in item;

export function useToc(
  legalTextSlug: MaybeRef<string | undefined>,
  paragraphSlug: MaybeRef<string | undefined>
) {
  const { query, enabled } = useFetchToc(legalTextSlug);
  const { data: nestedData } = query;

  const toc = computed(() =>
    unref(nestedData)?.flatMap((item) => flattenChildren(item))
  );

  const withoutHeaders = computed(() => toc.value?.filter(isParagraph));

  const activeIndex = computed(
    () =>
      withoutHeaders.value?.findIndex(
        (item) => item.slug === toValue(paragraphSlug)
      ) ?? -1
  );

  const previous = computed(
    () => withoutHeaders.value?.[activeIndex.value - 1]?.slug
  );

  const next = computed(
    () => withoutHeaders.value?.[activeIndex.value + 1]?.slug
  );

  const parentsOfActiveParagraph = computed(() =>
    parentsOfParagraph(toc.value, toValue(paragraphSlug))
  );

  return {
    nestedData,
    toc,
    parentsOfActiveParagraph,
    previous,
    next,
    tocLoading: query.isLoading,
    suspense: query.suspense,
    enabled,
  };
}
